import {
  type ChartTemplate,
  type ChartTemplateContent,
  type StudyTemplateData,
  type StudyTemplateMetaInfo,
} from 'charting_library';
import { omit } from 'es-toolkit/object';
import { createStore, del, get, keys, set } from 'idb-keyval';

import { type IMediator } from '#/core/mediator';

import { SystemError } from '#/shared/exceptions/system-error';
import { safeJsonParse } from '#/shared/utils/safe-json-parse';

import { SERVICES_TYPES } from '#/services-types';

import { type IDataFeedService } from '../data-feed';
import { type IDeviceService } from '../device';
import { type ILogService } from '../log';
import { type IUrlParamsService, UrlParamsName } from '../url-params';

import { type ITemplateStorageService, type TemplateKind } from './template-storage.types';

export class TemplateStorageService implements ITemplateStorageService {
  readonly #serviceName = 'TemplateStorageService';

  // idb-keyval opens the database without a version, so a second createStore against the
  // same database name would never create its object store. One store, prefixed keys.
  readonly #store = createStore('chart-templates-db', 'templates');

  #requestedSymbol: string | null = null;

  get #dataFeedService() {
    return this._mediator.get<IDataFeedService>(SERVICES_TYPES.DataFeedService);
  }

  get #deviceService() {
    return this._mediator.get<IDeviceService>(SERVICES_TYPES.DeviceService);
  }

  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }

  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }

  // ? Templates are per trading account: the same user must not share templates between accounts.
  get #accountNumber() {
    return this.#urlParamsService.getParamByName(UrlParamsName.AccountNumber);
  }

  constructor(private readonly _mediator: IMediator) {
    this.#logService.info(this.#serviceName, 'Instance was created.');
  }

  #logError(message: string, error?: unknown): void {
    this.#logService.error(this.#serviceName, error instanceof Error ? error : new SystemError(message));
  }

  async #namesByPrefix(prefix: string): Promise<string[]> {
    const storedKeys = await keys<string>(this.#store);

    return storedKeys.filter((key) => key.startsWith(prefix)).map((key) => key.slice(prefix.length));
  }

  #prefix(kind: TemplateKind, toolName?: string): string | null {
    const accountNumber = this.#accountNumber;

    if (!accountNumber) {
      this.#logService.warn(this.#serviceName, 'Missing "account-number" param in URL. Templates are unavailable.');

      return null;
    }

    return toolName ? `${kind}:${accountNumber}:${toolName}:` : `${kind}:${accountNumber}:`;
  }

  // Every adapter method has the same shape: resolve the account-scoped prefix, run one storage call,
  // fall back to the empty value when the account is missing or the store throws.
  async #withPrefix<T>({
    kind,
    toolName,
    fallback,
    errorMessage,
    action,
  }: {
    kind: TemplateKind;
    toolName?: string;
    fallback: T;
    errorMessage: string;
    action: (prefix: string) => Promise<T>;
  }): Promise<T> {
    const prefix = this.#prefix(kind, toolName);

    if (!prefix) return fallback;

    try {
      return await action(prefix);
    } catch (error) {
      this.#logError(errorMessage, error);

      return fallback;
    }
  }

  // --- Study templates

  getAllStudyTemplates(): Promise<StudyTemplateMetaInfo[]> {
    return this.#withPrefix<StudyTemplateMetaInfo[]>({
      kind: 'study',
      fallback: [],
      errorMessage: 'Failed to list study templates.',
      action: async (prefix) => (await this.#namesByPrefix(prefix)).map((name) => ({ name })),
    });
  }

  saveStudyTemplate({ name, content }: StudyTemplateData): Promise<void> {
    return this.#withPrefix({
      kind: 'study',
      fallback: undefined,
      errorMessage: `Failed to save study template ${name}.`,
      action: (prefix) => set(`${prefix}${name}`, content, this.#store),
    });
  }

  async getStudyTemplateContent({ name }: StudyTemplateMetaInfo): Promise<string> {
    const content = await this.#withPrefix({
      kind: 'study',
      fallback: '',
      errorMessage: `Failed to load study template ${name}.`,
      action: async (prefix) => (await get<string>(`${prefix}${name}`, this.#store)) ?? '',
    });

    return this.#withoutUnavailableSymbol({ name, content });
  }

  async #withoutUnavailableSymbol({ name, content }: { name: string; content: string }): Promise<string> {
    const template =
      safeJsonParse<Record<string, unknown>>(content, {
        onError: (error) => this.#logError(`Failed to parse study template ${name}.`, error),
      }) ?? {};

    const symbol = template.symbol;

    // ? Saved without "remember symbol" — the template is not bound to any symbol, there is nothing to drop.
    if (typeof symbol !== 'string') return content;

    if (this.#deviceService.parentApp) {
      this.#requestedSymbol = symbol;

      return JSON.stringify(omit(template, ['symbol', 'currency', 'unit']));
    }

    if (!(await this.#dataFeedService.symbolIsUnavailable(symbol))) return content;

    this.#logService.warn(this.#serviceName, `Symbol ${symbol} of study template ${name} is unavailable, dropped.`);

    return JSON.stringify(omit(template, ['symbol', 'currency', 'unit']));
  }

  takeRequestedSymbol(): string | null {
    const requestedSymbol = this.#requestedSymbol;

    this.#requestedSymbol = null;

    return requestedSymbol;
  }

  removeStudyTemplate({ name }: StudyTemplateMetaInfo): Promise<void> {
    return this.#withPrefix({
      kind: 'study',
      fallback: undefined,
      errorMessage: `Failed to remove study template ${name}.`,
      action: (prefix) => del(`${prefix}${name}`, this.#store),
    });
  }

  // --- Chart templates (colors)

  getAllChartTemplates(): Promise<string[]> {
    return this.#withPrefix<string[]>({
      kind: 'chart',
      fallback: [],
      errorMessage: 'Failed to list chart templates.',
      action: (prefix) => this.#namesByPrefix(prefix),
    });
  }

  saveChartTemplate(newName: string, theme: ChartTemplateContent): Promise<void> {
    return this.#withPrefix({
      kind: 'chart',
      fallback: undefined,
      errorMessage: `Failed to save chart template ${newName}.`,
      action: (prefix) => set(`${prefix}${newName}`, theme, this.#store),
    });
  }

  getChartTemplateContent(templateName: string): Promise<ChartTemplate> {
    return this.#withPrefix<ChartTemplate>({
      kind: 'chart',
      fallback: {},
      errorMessage: `Failed to load chart template ${templateName}.`,
      action: async (prefix) => {
        const content = await get<ChartTemplateContent>(`${prefix}${templateName}`, this.#store);

        return content ? { content } : {};
      },
    });
  }

  removeChartTemplate(templateName: string): Promise<void> {
    return this.#withPrefix({
      kind: 'chart',
      fallback: undefined,
      errorMessage: `Failed to remove chart template ${templateName}.`,
      action: (prefix) => del(`${prefix}${templateName}`, this.#store),
    });
  }

  // --- Drawing templates

  getDrawingTemplates(toolName: string): Promise<string[]> {
    return this.#withPrefix<string[]>({
      kind: 'drawing',
      toolName,
      fallback: [],
      errorMessage: `Failed to list drawing templates for ${toolName}.`,
      action: (prefix) => this.#namesByPrefix(prefix),
    });
  }

  saveDrawingTemplate(toolName: string, templateName: string, content: string): Promise<void> {
    return this.#withPrefix({
      kind: 'drawing',
      toolName,
      fallback: undefined,
      errorMessage: `Failed to save drawing template ${templateName}.`,
      action: (prefix) => set(`${prefix}${templateName}`, content, this.#store),
    });
  }

  loadDrawingTemplate(toolName: string, templateName: string): Promise<string> {
    return this.#withPrefix({
      kind: 'drawing',
      toolName,
      fallback: '',
      errorMessage: `Failed to load drawing template ${templateName}.`,
      action: async (prefix) => (await get<string>(`${prefix}${templateName}`, this.#store)) ?? '',
    });
  }

  removeDrawingTemplate(toolName: string, templateName: string): Promise<void> {
    return this.#withPrefix({
      kind: 'drawing',
      toolName,
      fallback: undefined,
      errorMessage: `Failed to remove drawing template ${templateName}.`,
      action: (prefix) => del(`${prefix}${templateName}`, this.#store),
    });
  }
}
